// src/components/ai/AiChatWidget.jsx
// Phase 3 — M3: Floating AI shopping assistant for buyers
// Mount this in DashboardLayout or BuyerLayout — it renders a persistent bubble

import { useEffect, useRef, useState } from 'react'
import { aiApi } from '../../api/services'

const COLORS = { primary: '#FF3D7F', secondary: '#FF8500', bg: '#FFF0F5' }

const STARTER_PROMPTS = [
  'I need cotton fabric for kurtas, 50 metres',
  'Show me GI-tagged Banarasi silk options',
  'How do I place a bulk B2B order?',
  'Track my recent order',
]

export default function AiChatWidget() {
  const [open, setOpen]         = useState(false)
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your Fabrinza AI assistant. I can help you find fabrics, place bulk orders, and answer any questions. What are you looking for today?" }
  ])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [sessionId]             = useState(() => `session_${Date.now()}`)
  const bottomRef               = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, open])

  async function send(text) {
    const msg = text || input.trim()
    if (!msg || loading) return
    setInput('')

    const userMsg = { role: 'user', content: msg }
    setMessages(m => [...m, userMsg])
    setLoading(true)

    try {
      const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }))
      const res = await aiApi.chat({ message: msg, session_id: sessionId })
      const reply = res.data?.data?.response

      const assistantMsg = {
        role: 'assistant',
        content: reply?.message || "I didn't quite get that. Could you rephrase?",
        intent: reply?.intent,
        searchQuery: reply?.search_query,
        action: reply?.action,
      }
      setMessages(m => [...m, assistantMsg])
    } catch (e) {
      setMessages(m => [...m, {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.',
      }])
    } finally {
      setLoading(false)
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }

  return (
    <>
      {/* Floating bubble */}
      <button
        onClick={() => setOpen(o => !o)}
        title="AI Shopping Assistant"
        style={{
          position: 'fixed', bottom: 24, right: 24,
          width: 54, height: 54, borderRadius: '50%',
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
          border: 'none', cursor: 'pointer', zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(255,61,127,0.4)',
          fontSize: '1.3rem',
          transition: 'transform 0.2s',
          transform: open ? 'scale(0.9)' : 'scale(1)',
        }}
      >{open ? '✕' : '🤖'}</button>

      {/* Chat window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 24,
          width: 360, height: 520,
          background: '#fff', borderRadius: 20,
          boxShadow: '0 16px 56px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          zIndex: 8999, overflow: 'hidden',
          border: '1.5px solid #F0F0F0',
        }}>
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
            padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🤖</div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>Fabrinza AI</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem' }}>Your textile shopping assistant</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '82%',
                  background: msg.role === 'user'
                    ? `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`
                    : COLORS.bg,
                  color: msg.role === 'user' ? '#fff' : '#374151',
                  padding: '10px 14px', borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  fontSize: '0.85rem', lineHeight: 1.55,
                }}>
                  {msg.content}
                  {msg.searchQuery && (
                    <a
                      href={`/catalogue?q=${encodeURIComponent(msg.searchQuery)}`}
                      style={{ display: 'block', marginTop: 8, background: '#fff', color: COLORS.primary, borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}
                    >
                      🔍 Search: {msg.searchQuery}
                    </a>
                  )}
                  {msg.action === 'open_rfq' && (
                    <a href="/buyer/rfq" style={{ display: 'block', marginTop: 8, background: '#fff', color: COLORS.primary, borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}>
                      📋 Create RFQ
                    </a>
                  )}
                  {msg.action === 'view_orders' && (
                    <a href="/buyer/orders" style={{ display: 'block', marginTop: 8, background: '#fff', color: COLORS.primary, borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}>
                      📦 My Orders
                    </a>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 5, padding: '8px 14px', background: COLORS.bg, borderRadius: '18px 18px 18px 4px', width: 'fit-content' }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.primary, opacity: 0.5, animation: `bounce 1s ease ${i*0.15}s infinite` }} />
                ))}
              </div>
            )}
            <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}`}</style>
            <div ref={bottomRef} />
          </div>

          {/* Starter prompts (first open) */}
          {messages.length === 1 && (
            <div style={{ padding: '0 14px 10px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {STARTER_PROMPTS.map((p, i) => (
                <button key={i} onClick={() => send(p)} style={{
                  padding: '5px 10px', borderRadius: 20, border: `1px solid ${COLORS.primary}`,
                  background: '#fff', color: COLORS.primary, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                }}>{p}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid #F0F0F0', display: 'flex', gap: 8 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about fabrics, orders, RFQs…"
              rows={1}
              style={{
                flex: 1, padding: '9px 12px', border: '1.5px solid #E5E7EB',
                borderRadius: 12, fontSize: '0.84rem', resize: 'none', outline: 'none',
                fontFamily: 'inherit', lineHeight: 1.4, maxHeight: 80, overflowY: 'auto',
              }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              style={{
                width: 38, height: 38, borderRadius: 10, border: 'none',
                background: input.trim() && !loading
                  ? `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`
                  : '#E5E7EB',
                color: '#fff', cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem', flexShrink: 0,
              }}
            >➤</button>
          </div>
        </div>
      )}
    </>
  )
}
